import React from "react";
import { Link } from "react-router-dom";

import Layout from "../../components/Layout/Layout";

const sharedExpectations = [
    {
        strong: "Step up",
        text: "apply yourself beyond what's asked and look for places to make additional impact.",
    },
    {
        strong: "Results oriented",
        text: "don't let anything prevent your success or the success of the team.",
    },
    {
        strong: "Proactive thinker",
        text: "identify and mitigate issues before they escalate to system impact.",
    },
    {
        strong: "Steadfast communicator",
        text: "challenge the status quo and push everyone to be their best.",
    },
    {
        strong: "Eager learner",
        text: "stay curious about new technologies and internalize them quickly.",
    },
    {
        strong: "Owner not follower",
        text: "demonstrate pride of ownership and a desire to deliver the best.",
    },
    {
        strong: "Team player",
        text: "help the business thrive across every branch.",
    },
    {
        strong: "High-speed",
        text: "thrive in fast-paced, dynamic environments. Index on action over talk.",
    },
    {
        strong: "Collaborate",
        text: "actively participate in planning to attain the highest quality output.",
    },
];

const aboutEverestParagraphs = [
    <>
        <a
            href="https://www.everest.org"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "var(--brand)" }}
        >
            Everest
        </a>{" "}
        is a global leader in FinTech and DeFi, disrupting the global
        financial industry by bringing the innovations of DeFi and FinTech to
        the mass market. Everest automates most transactions, leveraging eKYC
        and on-chain transaction monitoring to facilitate buy, trade, and
        sell crypto, plus on/off ramps for cross-border transactions in 45
        countries.
    </>,
    <>
        Everest is a licensed crypto custodian with its own high-speed
        blockchain and the world's first global and programmable stablecoin.
        As a VFA license holder, Everest is authorized to hold virtual
        assets, execute transactions, and transmit/receive orders. We offer
        global fiat in/out to crypto, a portfolio of financial services
        including crypto trading with access to 200+ coin pairs,
        international remittances, earning and borrowing, foreign exchange,
        and staking. Our biometrics-based digital identity platform empowers
        users to digitally verify their identity and selectively share
        proofs, credentials, and attributes.
    </>,
    <>
        If you want to change the world by revolutionizing financial
        services, identity, and value exchange — you should join Everest.
    </>,
];

const formatPostedDate = (iso) => {
    if (!iso) return null;
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

const JobPage = ({
    title,
    location = "Malta",
    type = "Full-time",
    status,
    statusNote,
    posted,
    summary,
    overview,
    tldr,
    tldrIntro,
    requirements,
    experienceNeeded,
    desiredExperience,
    whyJoin,
    applyInstructions,
    showValues = true,
    showAbout = true,
}) => {
    const closed = status === "closed";
    const postedLabel = formatPostedDate(posted);

    return (
        <Layout>
            <section className="page-hero">
                <div className="container">
                    <div className="eyebrow">
                        <span className="dot"></span> Careers
                    </div>
                    <h1>{title}</h1>
                    <p className="small-muted">
                        {location} &middot; {closed ? "This position is now closed" : type}
                    </p>
                    {postedLabel && (
                        <p
                            className="small-muted"
                            style={{ marginTop: 4 }}
                        >
                            Posted{" "}
                            <time dateTime={posted}>{postedLabel}</time>
                        </p>
                    )}
                    {statusNote && (
                        <p className="small-muted" style={{ marginTop: 4 }}>
                            {statusNote}
                        </p>
                    )}
                    <p style={{ marginTop: 12 }}>
                        <Link
                            to="/careers"
                            style={{
                                color: "var(--brand)",
                                fontWeight: 600,
                                textDecoration: "underline",
                            }}
                        >
                            &larr; All open positions
                        </Link>
                    </p>
                </div>
            </section>

            <section className="section section-tight">
                <div className="container">
                    {overview && (
                        <article className="card" style={{ marginBottom: 18 }}>
                            <span className="kicker">Role overview</span>
                            <h2 style={{ marginTop: 4, marginBottom: 12, fontSize: "1.25rem" }}>
                                About this role
                            </h2>
                            {(Array.isArray(overview) ? overview : [overview]).map((p, i) => (
                                <p key={i} style={{ marginTop: i === 0 ? 0 : 12 }}>
                                    {p}
                                </p>
                            ))}
                        </article>
                    )}

                    <div className="grid-2">
                        {summary && summary.length > 0 && (
                            <article className="card">
                                <span className="kicker">Summary of position</span>
                                <h2 style={{ marginTop: 4, marginBottom: 12, fontSize: "1.25rem" }}>
                                    What you'll do
                                </h2>
                                <ul className="check-list micro-list">
                                    {summary.map((s, i) => (
                                        <li key={i}>{s}</li>
                                    ))}
                                </ul>
                            </article>
                        )}

                        {tldr && tldr.length > 0 && (
                            <article className="card">
                                <span className="kicker">TL;DR</span>
                                <h2 style={{ marginTop: 4, marginBottom: 12, fontSize: "1.25rem" }}>
                                    {tldrIntro || "You should know"}
                                </h2>
                                <ul className="check-list micro-list">
                                    {tldr.map((s, i) => (
                                        <li key={i}>{s}</li>
                                    ))}
                                </ul>
                            </article>
                        )}
                    </div>

                    {requirements && requirements.length > 0 && (
                        <article className="card" style={{ marginTop: 18 }}>
                            <span className="kicker">Requirements & qualifications</span>
                            <h2 style={{ marginTop: 4, marginBottom: 12, fontSize: "1.25rem" }}>
                                What we're looking for
                            </h2>
                            <ul className="check-list micro-list">
                                {requirements.map((s, i) => (
                                    <li key={i}>{s}</li>
                                ))}
                            </ul>

                            {(experienceNeeded || desiredExperience) && (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: 18,
                                        marginTop: 18,
                                    }}
                                    className="job-experience-grid"
                                >
                                    {experienceNeeded && experienceNeeded.length > 0 && (
                                        <div>
                                            <p style={{ fontWeight: 700, marginBottom: 6 }}>
                                                Experience needed
                                            </p>
                                            <ul className="check-list micro-list">
                                                {experienceNeeded.map((s, i) => (
                                                    <li key={i}>{s}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {desiredExperience && desiredExperience.length > 0 && (
                                        <div>
                                            <p style={{ fontWeight: 700, marginBottom: 6 }}>
                                                Desired experience
                                            </p>
                                            <ul className="check-list micro-list">
                                                {desiredExperience.map((s, i) => (
                                                    <li key={i}>{s}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </article>
                    )}

                    {showValues && (
                        <article className="card" style={{ marginTop: 18 }}>
                            <span className="kicker">Our values</span>
                            <h2 style={{ marginTop: 4, marginBottom: 12, fontSize: "1.25rem" }}>
                                We expect this of all Everest employees
                            </h2>
                            <ul className="check-list micro-list">
                                {sharedExpectations.map((e, i) => (
                                    <li key={i}>
                                        <strong>{e.strong}</strong> — {e.text}
                                    </li>
                                ))}
                            </ul>
                        </article>
                    )}

                    {whyJoin && whyJoin.length > 0 && (
                        <article className="card" style={{ marginTop: 18 }}>
                            <span className="kicker">Why Everest</span>
                            <h2 style={{ marginTop: 4, marginBottom: 12, fontSize: "1.25rem" }}>
                                Why join Everest.org?
                            </h2>
                            <ul className="check-list micro-list">
                                {whyJoin.map((w, i) => (
                                    <li key={i}>
                                        {w.title && <strong>{w.title}</strong>}
                                        {w.title ? " — " : ""}
                                        {w.text}
                                    </li>
                                ))}
                            </ul>
                        </article>
                    )}

                    {showAbout && (
                        <article className="card" style={{ marginTop: 18 }}>
                            <span className="kicker">About Everest</span>
                            <h2 style={{ marginTop: 4, marginBottom: 12, fontSize: "1.25rem" }}>
                                Who we are
                            </h2>
                            {aboutEverestParagraphs.map((p, i) => (
                                <p key={i} style={{ marginTop: i === 0 ? 0 : 12 }}>
                                    {p}
                                </p>
                            ))}
                        </article>
                    )}

                    {!closed && (
                        <article
                            className="card dark-band"
                            style={{ marginTop: 18 }}
                        >
                            <span className="kicker">How to apply</span>
                            <h3>Send us your application</h3>
                            {applyInstructions ? (
                                <p>{applyInstructions}</p>
                            ) : (
                                <p>
                                    Interested candidates should submit a cover
                                    letter, CV, and any other materials
                                    (portfolio or work samples) to{" "}
                                    <a
                                        href={`mailto:careers@everest.org?subject=Everest Careers — ${encodeURIComponent(
                                            title
                                        )}`}
                                        style={{
                                            color: "#fff",
                                            textDecoration: "underline",
                                        }}
                                    >
                                        careers@everest.org
                                    </a>{" "}
                                    with the subject line "Everest Careers — {title}".
                                </p>
                            )}
                        </article>
                    )}
                </div>
            </section>
        </Layout>
    );
};

export default JobPage;
